import type { TellescopeModelFromName } from "../../mappings";
import { useUsersQuery } from "./use-users-query";
import { useMemo } from "react";

/**
 * Fetches users and returns them as a map/dictionary keyed by user ID.
 *
 * This hook wraps `useUsersQuery` and transforms the result array into
 * a record object for efficient O(1) lookups by user ID. The transformation
 * is memoized to prevent unnecessary recalculations.
 *
 * @param userIds - Optional array of user IDs to filter by. If provided and empty,
 *                  the query is disabled and returns an empty map. If undefined,
 *                  all users are loaded and mapped.
 *
 * @returns A record object mapping user IDs to user records. Returns an empty
 *          object if:
 *   - The query is disabled (empty userIds array)
 *   - The query is still loading
 *   - No users are found
 *
 * @example
 * ```tsx
 * // Create a map of all users
 * const usersMap = useUsersMap();
 * const user = usersMap['user123']; // O(1) lookup
 *
 * // Create a map for specific users
 * const selectedUsersMap = useUsersMap(['user1', 'user2', 'user3']);
 * if (selectedUsersMap['user1']) {
 *   console.log(selectedUsersMap['user1'].name);
 * }
 *
 * // Empty map when disabled
 * const emptyMap = useUsersMap([]); // Returns {}, query disabled
 * ```
 *
 * @remarks
 * - Result is memoized and only recalculated when query data changes
 * - Ideal for components that need frequent user lookups by ID
 * - More efficient than searching through an array for each lookup
 * - Returns empty object instead of undefined for safer destructuring
 */
export const useUsersMap = (userIds?: string[]) => {
  const query = useUsersQuery(userIds);
  return useMemo<Record<string, TellescopeModelFromName<"users">>>(() => {
    return (
      query.data?.reduce<Record<string, TellescopeModelFromName<"users">>>((acc, user) => {
        acc[user.id] = user;
        return acc;
      }, {}) ?? {}
    );
  }, [query.data]);
};
